import styled from '@emotion/styled';
import { Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

const Chip = styled.div<{ checked: boolean; expandable?: boolean }>`
    display: flex;
    width: 355px;
    padding: 18px 16px;
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    border-radius: 8px;
    border: ${({ checked, theme }) => (checked ? `1.5px solid ${theme.colors.primary.main}` : `1px solid ${theme.colors.black[400]}`)};
    background-color: ${({ checked }) => (checked ? `rgba(255, 136, 46, 0.15)` : ``)};
    color: ${({ checked, theme }) => (checked ? theme.colors.secondary.main : theme.colors.secondary[700])};
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: 21px;
    height: ${({ checked, expandable }) => (checked && expandable ? '97px' : '57px')};
    transition: all 0.25s ease-in-out;

    :hover {
        color: ${({ theme }) => theme.colors.secondary.main};
        border: 1px solid ${({ theme, checked }) => (checked ? `1.5px solid ${theme.colors.primary.main}` : theme.colors.black[600])};
        background: ${({ theme, checked }) => (checked ? `rgba(255, 136, 46, 0.15)` : theme.colors.black[50])};
        cursor: pointer;
        height: ${({ expandable }) => (expandable ? '97px' : '57px')};
    }
`;

const SubText = styled(Typography)`
    color: ${({ theme }) => theme.colors.secondary[600]};
    font-size: 14px;
    font-style: normal;
    font-weight: 500;
    line-height: 16px;
`;

const Text = styled.div<{ show: boolean }>`
    display: ${({ show }) => (show ? 'flex' : 'none')};
    flex-direction: row;
    gap: 8px;
    align-items: center;
`;

const Content = styled.div`
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 6px;
    text-align: start;
`;

const StyledInput = styled.input<{ show: boolean; empty: boolean }>`
    display: ${({ show }) => (show ? 'inline' : 'none')};
    width: 100%;
    height: 21px;
    border: none;
    background: transparent;
    color: ${({ theme, empty }) => (empty ? theme.colors.secondary[500] : theme.colors.secondary.main)};
    font-size: 16px;
    font-weight: 500;
    line-height: 21px;

    :focus {
        outline: none;
    }
`;

interface Props {
    value: {
        label: string;
        explanation?: string;
    };
    onClick: VoidFunction;
    checked: boolean;
    className?: string;
    isMultiple?: boolean;
    isText?: boolean;
    setText?: (message: string) => any;
    icon?: string;
}

export default function ExpandedChip({ value, onClick, checked, className, isText, setText, icon }: Props) {
    const [isHovering, setIsHovering] = useState(false);
    const [message, setMessage] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);

    const handleMouseOver = () => {
        setIsHovering(true);
    };

    const handleMouseOut = () => {
        setIsHovering(false);
    };

    const handleChange = (event: any) => {
        setMessage(event.target.value);
        setText?.(message);
    };

    const handleInputClick = () => {
        setTimeout(() => {
            inputRef.current?.focus();
        }, 100);
        onClick();
    };

    return (
        <Chip
            onClick={handleInputClick}
            checked={checked}
            className={className}
            onMouseOver={handleMouseOver}
            onMouseOut={handleMouseOut}
            expandable={!!value?.explanation}>
            <Content>
                <Text show={!isText || (!!isText && !checked)}>
                    {icon && <MaskedIcon src={icon} size="24px" />}
                    {value.label}
                    {value?.explanation && (isHovering || checked) && <SubText>{value?.explanation}</SubText>}
                </Text>
                <StyledInput
                    show={!!isText && checked}
                    empty={message === ''}
                    placeholder="Please describe..."
                    ref={inputRef}
                    value={message}
                    onChange={handleChange}
                />
            </Content>
        </Chip>
    );
}
